<template>
  <div class="flex min-h-screen transition-colors duration-700 ease-in-out dark:bg-gray-900" :class="isAuthenticated ? 'bg-gray-100' : 'bg-slate-200'">
    <!-- Overlay de transição de tema (efeito pincel) -->
    <Transition enter-active-class="transition-opacity duration-300 ease-in" leave-active-class="transition-opacity duration-500 ease-out" enter-from-class="opacity-0" leave-to-class="opacity-0">
      <div v-if="isTransitioning" class="pointer-events-none fixed inset-0 z-[10000] overflow-hidden" aria-hidden="true">
        <div class="absolute inset-0 flex items-center justify-center">
          <img :src="logoImage" alt="" class="h-24 w-auto max-w-[300px] object-contain opacity-50 animate-pulse-slow" />
        </div>
        <div class="absolute inset-0 w-[150%] animate-brush-sweep"
          :class="isDark ? 'bg-gradient-to-r from-transparent via-gray-900/95 to-gray-900' : 'bg-gradient-to-r from-transparent via-white/95 to-white'"></div>
        <div class="absolute inset-0 w-[150%] animate-brush-sweep opacity-40 [animation-delay:150ms]"
          :class="isDark ? 'bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent' : 'bg-gradient-to-r from-transparent via-orange-300/30 to-transparent'"></div>
      </div>
    </Transition>

    <!-- ====== BARRA SUPERIOR ====== -->
    <header v-if="isAuthenticated" class="fixed inset-x-0 top-0 z-50 border-b border-gray-200 bg-white shadow-sm transition-colors duration-700 dark:border-gray-700 dark:bg-gray-800 print:hidden">
      <div class="flex h-16 items-center justify-between gap-3 px-3 sm:px-4 md:px-6">
        <div class="flex min-w-0 items-center">
          <button type="button" class="btn-ghost mr-1 hidden p-2 md:inline-flex" :aria-label="isCollapsed ? 'Expandir menu' : 'Recolher menu'" @click="toggleSidebar">
            <Menu class="h-5 w-5" />
          </button>
          <button type="button" class="btn-ghost mr-1 p-2 md:hidden" aria-label="Abrir menu" @click="toggleSidebar">
            <Menu class="h-5 w-5" />
          </button>

          <button type="button" class="flex shrink-0 items-center" aria-label="Ir para novidades" @click="route.push('/novidades')">
            <Transition mode="out-in" enter-active-class="transition-all duration-500" leave-active-class="transition-all duration-500" enter-from-class="opacity-0 scale-90" leave-to-class="opacity-0 scale-90">
              <img :key="isDark ? 'dark' : 'light'" :src="logoImage" alt="Logo CCDTI" class="mr-2 w-auto max-w-[160px] object-contain sm:max-w-[200px]" :class="isDark ? 'h-9 sm:h-10' : 'h-6 sm:h-7'" />
            </Transition>
          </button>
        </div>

        <!-- Título da página (mobile) -->
        <h2 class="min-w-0 flex-1 truncate text-center text-base font-semibold text-gray-800 dark:text-white md:hidden">
          {{ pageTitle }}
        </h2>

        <div class="flex shrink-0 items-center gap-1 sm:gap-3">
          <div class="relative" data-notification-menu>
            <NotificationsPage
              @open-full-history="navigateToNotificationsHistory"
              :is-collapsed="false"
              :are-notifications-enabled="areNotificationsEnabled"
              class="flex items-center"
            />
          </div>

          <div class="relative" data-user-menu>
            <button type="button" class="flex items-center rounded-full p-1 transition hover:bg-gray-100 dark:hover:bg-gray-700" data-user-menu-button aria-haspopup="menu" :aria-expanded="isUserMenuOpen" @click="toggleUserMenu">
              <div v-if="!profileImage" class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-300 text-sm font-bold text-gray-800 dark:bg-gray-600 dark:text-gray-200">
                {{ getUserInitials }}
              </div>
              <img v-else :src="profileImage" :alt="getUserName" class="h-8 w-8 shrink-0 rounded-full object-cover" @error="handleImageError" />
              <span class="ml-2 hidden max-w-[10rem] truncate font-medium text-gray-800 dark:text-gray-200 md:block">{{ getUserName }}</span>
              <component :is="isUserMenuOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" class="ml-1 hidden h-4 w-4 shrink-0 text-gray-800 dark:text-gray-200 md:block" />
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- ====== SIDEBAR ====== -->
    <template v-if="isAuthenticated">
      <!-- Fundo escuro quando a sidebar está aberta no mobile -->
      <Transition enter-active-class="transition-opacity duration-200" leave-active-class="transition-opacity duration-200" enter-from-class="opacity-0" leave-to-class="opacity-0">
        <div v-if="isSidebarOpen" class="fixed inset-0 z-30 bg-black/40 md:hidden" aria-hidden="true" @click="isSidebarOpen = false"></div>
      </Transition>

      <aside
        class="fixed inset-y-0 left-0 z-40 mt-16 flex flex-col border-r border-gray-200 bg-white text-gray-700 transition-all duration-300 ease-out dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 print:hidden"
        :class="[
          isCollapsed ? 'md:w-20' : 'md:w-64',
          'w-64',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        ]"
        aria-label="Menu principal"
      >
        <div class="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
          <nav class="space-y-1 p-3">
            <a
              v-for="item in navItems"
              :key="item.name"
              :href="item.href"
              class="group flex items-center rounded-lg p-3 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              :class="[
                isCollapsed ? 'md:justify-center' : '',
                isActive(item.href) ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100' : ''
              ]"
              :title="isCollapsed ? item.name : ''"
              :aria-current="isActive(item.href) ? 'page' : undefined"
            >
              <div class="flex flex-col items-center justify-center">
                <component :is="item.icon" class="h-5 w-5 shrink-0 transition-transform duration-200" :class="{ 'md:group-hover:scale-110': isCollapsed }" />
                <span v-if="item.beta && isCollapsed" class="mt-1 hidden text-[10px] font-bold text-orange-600 dark:text-orange-400 md:block">BETA</span>
              </div>
              <div class="ml-3 overflow-hidden transition-all duration-300 ease-out" :class="isCollapsed ? 'md:ml-0 md:w-0 md:opacity-0' : 'w-40 opacity-100'">
                <div class="flex flex-col">
                  <span class="whitespace-nowrap">{{ item.name }}</span>
                  <span v-if="item.beta" class="text-[10px] font-semibold text-orange-500">BETA</span>
                </div>
              </div>
            </a>
          </nav>
        </div>

        <div class="flex-none border-t border-gray-200 px-4 py-4 dark:border-gray-700">
          <div class="mb-3 flex justify-center">
            <Transition enter-active-class="transition-all duration-500" leave-active-class="transition-all duration-300" enter-from-class="translate-x-10 opacity-0" leave-to-class="translate-x-10 opacity-0">
              <img v-if="!isHomeRoute" :src="zegotinhaImage" alt="Zé Gotinha" class="h-20 w-auto object-contain animate-float" />
            </Transition>
          </div>
          <button type="button" class="hidden w-full items-center justify-center rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 md:flex" @click="toggleSidebar">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 transition-transform duration-300" :class="{ 'rotate-180': isCollapsed }" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            <span class="ml-2 overflow-hidden text-sm font-medium transition-all duration-300 ease-out" :class="isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'">Recolher Menu</span>
          </button>
          <button type="button" class="btn-secondary w-full md:hidden" @click="isSidebarOpen = false">Fechar menu</button>
        </div>
      </aside>
    </template>

    <!-- ====== CONTEÚDO ====== -->
    <div
      class="relative z-10 flex min-w-0 flex-1 flex-col bg-slate-200 transition-all duration-300 ease-in-out dark:bg-gray-900 print:m-0 print:bg-white print:p-0"
      :class="[isAuthenticated ? (isCollapsed ? 'md:ml-20 print:ml-0' : 'md:ml-64 print:ml-0') : '', isAuthenticated ? 'mt-16 pb-20 md:pb-0 print:mt-0 print:pb-0' : '']"
    >
      <main class="flex flex-1 flex-col bg-slate-200 dark:bg-gray-900 print:bg-white">
        <div v-if="pageTitle" class="hidden md:block">
          <h2 class="text-xl font-semibold text-gray-800 dark:text-white">{{ pageTitle }}</h2>
        </div>

        <div class="min-w-0 flex-1">
          <slot name="content">
            <div class="text-center text-gray-500 dark:text-gray-400">Nada para mostrar</div>
          </slot>
        </div>

        <div class="flex w-full justify-center py-3 print:hidden">
          <img
            :src="logoPcrjSaude"
            alt="Prefeitura do Rio - Saúde - SUS"
            class="h-7 w-auto object-contain transition-all duration-300 md:h-10 lg:h-12"
            :class="isDark ? 'opacity-90' : 'opacity-85 [filter:brightness(0)_saturate(100%)_invert(14%)_sepia(77%)_saturate(1784%)_hue-rotate(192deg)_brightness(92%)_contrast(97%)]'"
          />
        </div>
      </main>
    </div>

    <!-- ====== NAVEGAÇÃO INFERIOR (MOBILE) ====== -->
    <nav v-if="isAuthenticated" class="fixed inset-x-0 bottom-0 z-40 w-full overflow-hidden border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 print:hidden md:hidden" aria-label="Navegação principal (mobile)">
      <div ref="navContainer" class="relative flex w-full max-w-[100vw] overflow-x-auto whitespace-nowrap bg-white/90 pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_10px_rgba(0,0,0,0.05)] backdrop-blur-md scrollbar-none dark:bg-gray-800/90">
        <a
          v-for="item in navItems.slice(0, 5)"
          :key="item.name"
          :href="item.href"
          class="flex min-w-[4.5rem] flex-grow flex-col items-center justify-center px-3 py-2.5 transition-all duration-200 active:scale-95"
          :class="isActive(item.href) ? 'bg-blue-50/70 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'"
          :title="item.name"
          :aria-current="isActive(item.href) ? 'page' : undefined"
        >
          <component :is="item.icon" class="mb-1 h-6 w-6 transition-transform" :class="isActive(item.href) ? 'scale-110' : ''" />
          <span class="text-[11px] font-medium tracking-tight">{{ item.name }}</span>
          <span v-if="item.beta" class="text-[10px] font-bold text-orange-600 dark:text-orange-400">BETA</span>
        </a>

        <button type="button" data-user-menu-mobile data-user-menu-mobile-button class="group relative flex min-w-[4.5rem] flex-grow flex-col items-center justify-center px-3 py-2.5 transition-all duration-200 active:scale-95" title="Mais" @click="toggleMobileUserMenu">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="stroke-current text-gray-500 transition-colors group-hover:text-blue-500 dark:text-gray-200 dark:group-hover:text-blue-300" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="4" x2="20" y1="12" y2="12" /><line x1="4" x2="20" y1="6" y2="6" /><line x1="4" x2="20" y1="18" y2="18" />
          </svg>
          <span class="text-[11px] font-medium tracking-tight text-gray-500 group-hover:text-blue-500 dark:text-gray-400 dark:group-hover:text-blue-300">Mais</span>
        </button>
      </div>
    </nav>

    <!-- ====== MENU DO USUÁRIO (DESKTOP) ====== -->
    <div class="relative" ref="userMenu">
      <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-200 ease-in" enter-from-class="translate-y-4 scale-90 opacity-0" leave-to-class="translate-y-4 scale-90 opacity-0">
        <div v-if="isUserMenuOpen" class="fixed right-4 top-16 z-[9999] w-72 rounded-2xl border border-gray-100 bg-white px-2 py-4 text-gray-800 shadow-2xl ring-2 ring-blue-100 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:ring-gray-700" role="menu">
          <div class="space-y-1">
            <button type="button" role="menuitem" class="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold text-blue-700 transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-800" @click="irParaMeuPerfil">
              <User class="mr-3 h-5 w-5 text-blue-600 dark:text-blue-400" /> Meu Perfil
            </button>
            <button v-if="userStore.grupos && userStore.grupos.includes('superadmin')" type="button" role="menuitem" class="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold text-blue-700 transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-800" @click="irParaSuperadmin">
              <UserCog class="mr-3 h-5 w-5 text-blue-600 dark:text-blue-400" /> Painel Superadmin
            </button>
            <button type="button" role="menuitem" class="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold transition hover:bg-gray-100 dark:hover:bg-gray-700" @click="toggleDarkMode">
              <component :is="isDark ? SunIcon : MoonIcon" class="mr-3 h-5 w-5 text-gray-500 dark:text-gray-300" /> {{ isDark ? "Modo Claro" : "Modo Escuro" }}
            </button>
            <button type="button" role="menuitem" class="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold transition"
              :class="areNotificationsEnabled ? 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-800 dark:text-red-300 dark:hover:bg-red-700' : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-800 dark:text-green-300 dark:hover:bg-green-700'"
              @click="toggleNotifications">
              <Bell class="mr-3 h-5 w-5" :class="areNotificationsEnabled ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'" />
              {{ areNotificationsEnabled ? "Desabilitar Notificações" : "Habilitar Notificações" }}
            </button>
            <button type="button" role="menuitem" class="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold text-blue-700 transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-800" @click="openPasswordChangeModal">
              <Lock class="mr-3 h-5 w-5 text-blue-600 dark:text-blue-400" /> Trocar Senha
            </button>
            <button type="button" role="menuitem" class="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold text-blue-700 transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-800" @click="irParaNovidades">
              <FileText class="mr-3 h-5 w-5 text-blue-600 dark:text-blue-400" /> Novidades do sistema
            </button>
            <button type="button" role="menuitem" class="flex w-full items-center rounded-xl px-4 py-3 text-left text-sm font-semibold text-red-700 transition hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-800" @click="handleLogout">
              <LogOut class="mr-3 h-5 w-5 text-red-600 dark:text-red-400" /> Logout
            </button>
          </div>
        </div>
      </Transition>
    </div>

    <!-- ====== MENU DO USUÁRIO (MOBILE) ====== -->
    <div class="relative" ref="mobileUserMenu">
      <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-200 ease-in" enter-from-class="translate-y-6 opacity-0" leave-to-class="translate-y-6 opacity-0">
        <div v-if="isMobileUserMenuOpen" class="fixed inset-x-0 bottom-16 z-[9999] mx-2 flex max-h-[70vh] flex-col rounded-t-2xl border border-gray-100 bg-white shadow-2xl ring-2 ring-blue-100 dark:border-gray-700 dark:bg-gray-800 dark:ring-gray-700" role="menu">
          <div class="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
            <h3 class="font-semibold text-gray-800 dark:text-gray-200">Menu</h3>
            <button type="button" class="rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Fechar menu" @click="toggleMobileUserMenu">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-500 dark:text-gray-400" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div class="flex-1 overflow-y-auto overscroll-contain p-3">
            <div class="flex flex-col space-y-2">
              <a v-for="item in navItems.slice(5)" :key="item.name" :href="item.href" role="menuitem"
                class="flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold transition"
                :class="isActive(item.href) ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'">
                <span class="flex items-center">
                  <component :is="item.icon" class="mr-3 h-5 w-5" :class="isActive(item.href) ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-300'" />
                  {{ item.name }}
                </span>
                <span v-if="item.beta" class="text-[10px] font-bold text-orange-600 dark:text-orange-400">BETA</span>
              </a>

              <div class="my-2 border-t border-gray-200 dark:border-gray-700"></div>

              <button type="button" role="menuitem" class="flex w-full items-center rounded-xl bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-800" @click="irParaMeuPerfil">
                <User class="mr-3 h-5 w-5 text-blue-600 dark:text-blue-400" /> Meu Perfil
              </button>
              <button type="button" role="menuitem" class="flex w-full items-center rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600" @click="toggleDarkMode">
                <component :is="isDark ? SunIcon : MoonIcon" class="mr-3 h-5 w-5 text-gray-500 dark:text-gray-300" /> {{ isDark ? "Modo Claro" : "Modo Escuro" }}
              </button>
              <button type="button" role="menuitem" class="flex w-full items-center rounded-xl px-4 py-3 text-sm font-semibold transition"
                :class="areNotificationsEnabled ? 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-800 dark:text-red-300 dark:hover:bg-red-700' : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-800 dark:text-green-300 dark:hover:bg-green-700'"
                @click="toggleNotifications">
                <Bell class="mr-3 h-5 w-5" :class="areNotificationsEnabled ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'" />
                {{ areNotificationsEnabled ? "Desabilitar Notificações" : "Habilitar Notificações" }}
              </button>
              <button type="button" role="menuitem" class="flex w-full items-center rounded-xl bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-800" @click="openPasswordChangeModal">
                <Lock class="mr-3 h-5 w-5 text-blue-600 dark:text-blue-400" /> Trocar Senha
              </button>
              <button type="button" role="menuitem" class="flex w-full items-center rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-800" @click="handleLogout">
                <LogOut class="mr-3 h-5 w-5 text-red-600 dark:text-red-400" /> Logout
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <!-- ====== MODAL: TROCAR SENHA ====== -->
    <Transition name="fade">
      <div v-if="isPasswordChangeModalOpen" class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="titulo-trocar-senha">
        <div class="card w-full max-w-md p-6 animate-scale-in">
          <h2 id="titulo-trocar-senha" class="mb-4 text-xl font-bold text-gray-800 dark:text-gray-200">Trocar Senha</h2>

          <div v-if="successMessage" class="mb-4 rounded border-l-4 border-green-500 bg-green-100 p-4 text-green-700">{{ successMessage }}</div>
          <div v-if="errorMessage" class="mb-4 rounded border-l-4 border-red-500 bg-red-100 p-4 text-red-700">{{ errorMessage }}</div>

          <form class="space-y-4" @submit.prevent="handleChangePassword">
            <div>
              <label for="current-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Senha Atual</label>
              <input v-model="currentPassword" type="password" id="current-password" required autocomplete="current-password" class="input mt-1" />
            </div>
            <div>
              <label for="new-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Nova Senha</label>
              <input v-model="newPassword" type="password" id="new-password" required autocomplete="new-password" class="input mt-1" />
            </div>
            <div>
              <label for="confirm-password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirme a Nova Senha</label>
              <input v-model="confirmPassword" type="password" id="confirm-password" required autocomplete="new-password" class="input mt-1" />
            </div>
            <div class="flex justify-end gap-3 pt-2">
              <button type="button" class="btn-secondary" @click="closePasswordChangeModal">Cancelar</button>
              <button type="submit" class="btn-primary">Salvar</button>
            </div>
          </form>
        </div>
      </div>
    </Transition>

    <!-- ====== POP-UP DE NOTIFICAÇÃO (DESKTOP) ====== -->
    <Transition name="fade">
      <div v-if="isPopupVisible && !isMobile" class="card fixed bottom-4 right-4 z-50 w-80 p-4 text-gray-900 dark:text-gray-200" role="status">
        <div class="flex items-start gap-4">
          <Bell class="h-6 w-6 shrink-0 text-blue-500" />
          <div class="min-w-0 flex-1">
            <h3 class="text-sm font-semibold">{{ popupMessage.title }}</h3>
            <p class="text-sm" v-html="formatNotificationPopup(popupMessage.body)"></p>
          </div>
          <button type="button" class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" aria-label="Fechar" @click="closePopup">✕</button>
        </div>
      </div>
    </Transition>

    <!-- ====== NOTIFICAÇÃO ESTILO PUSH (MOBILE) ====== -->
    <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-300 ease-in" enter-from-class="-translate-y-full opacity-0" leave-to-class="-translate-y-full opacity-0">
      <div v-if="isPopupVisible && isMobile" class="fixed left-1/2 top-20 z-50 flex w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 items-center gap-3 rounded-lg bg-gray-900 p-3 text-white shadow-lg dark:bg-gray-200 dark:text-gray-900" role="status">
        <Bell class="h-6 w-6 shrink-0 text-blue-500" />
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-semibold">{{ popupMessage.title }}</h3>
          <p class="text-xs" v-html="formatNotificationPopup(popupMessage.body)"></p>
        </div>
        <button type="button" class="text-gray-400 hover:text-gray-200 dark:hover:text-gray-800" aria-label="Fechar" @click="closePopup">✕</button>
      </div>
    </Transition>
  </div>
  <TermoNotification :show="isTermoNotificationVisible" @confirmar="aceitarNotificacoes" @cancelar="recusarNotificacoes" />
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router';
import { ref, onMounted, computed, onBeforeUnmount, defineProps, watch } from 'vue';
import TermoNotification from '@/views/notifications/TermoNotification.vue'
import { usePerfilStore } from '@/stores/settings/perfilStore'
import { logout, getUser } from '@/services/authService';
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import {
  FileText,
  LogOut,
  Lock,
  Bell,
  SunIcon,
  MoonIcon,
  MessageCircle,
  Shield,
  Cloud,
  TrendingUp,
  Database,
  Hourglass,
  Settings,
  MonitorCog,
  Handshake,
  House,
  User,
  LayoutDashboard,
  Globe,
  FileWarning,
  Scale,
  UserCog,
  Menu,
  ClipboardList,
 // ClipboardCheck,

} from 'lucide-vue-next';

import apiClient from '@/services/authService'
import { useUserStore } from '@/stores/userStore';
import NotificationsPage from '../notifications/NotificationPage.vue';
import logoCcdti from '@/assets/logo_ccdti.png';
import logoCcdtiBranco from '@/assets/logo_ccdti_branco.png';
import zegotinhaImage from '@/assets/zegotinha.png';
import logoPcrjSaude from '@/assets/SaudePublicaCarioca_Logo_HorizontalEstendida_Branco.png';

const userStore = useUserStore();
const isAuthenticated = computed(() => userStore.autenticado);

const props = defineProps({
  appName: {
    type: String,
    default: 'Dashboard CCDTI'
  },
  pageTitle: {
    type: String,
    default: 'Dashboard'
  },
  userAvatar: {
    type: String,
    default: null
  },
  customNavItems: {
    type: Array,
    default: () => []
  }
});

const termoAceito = ref(false);
const route = useRouter();
const currentRoute = useRoute();
const isTermoNotificationVisible = ref(false);
const isDark = ref(false);
const isTransitioning = ref(false);
const isUserMenuOpen = ref(false);
const isMobileUserMenuOpen = ref(false);
const isNotificationMenuOpen = ref(false);
const isSidebarOpen = ref(false);
const isCollapsed = ref(true);
const userType = ref('admin'); // Exemplos: 'admin', 'manager', 'user', etc.

const isPasswordChangeModalOpen = ref(false);
const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const errorMessage = ref('');
const successMessage = ref('');

const perfil = usePerfilStore();
const user = useUserStore();
const areNotificationsEnabled = computed({
  get: () => user.notifications,
  set: (val) => user.setNotificationsStatus(val)
});

const isHomeRoute = computed(() => currentRoute.name === 'HomePage');

// Função para aceitar as notificações
const aceitarNotificacoes = async () => {
  isTermoNotificationVisible.value = false;

  try {
    await apiClient.post('/api/v1/audit/accept/notify/', {
      detalhes: `Usuário aceitou o termo de notificações em ${new Date().toLocaleString('pt-BR')}`,
    });

    termoAceito.value = true;
    areNotificationsEnabled.value = true;
    perfil.setNotificationsStatus(true);

    await subscribeUser();
    console.log('Termo aceito e registrado.');
  } catch (error) {
    console.error('Erro ao registrar aceite do termo:', error);
  }
};

// Função para recusar as notificações
const recusarNotificacoes = async () => {
  isTermoNotificationVisible.value = false;
  
  try {
    await apiClient.post('/api/v1/audit/reject/notify/');
    areNotificationsEnabled.value = false; // Atualiza o estado no Pinia
    console.log('Termo recusado e registrado.');
  } catch (error) {
    console.error('Erro ao registrar recusa do termo:', error);
  }
};



const irParaMeuPerfil = () => {
  route.push({ name: 'MeuPerfil' }); 
};

const irParaNovidades = () => {
  route.push('/novidades');
};

const irParaSuperadmin = () => {
  if (userStore.grupos.includes('superadmin')) {
    window.location.href = 'https://inovaapi.ccdti.com.br/admin/';
  } else {
    route.push({ name: 'DashboardEquipamentos', query: { unauthorized: true } });
  }
};


const getUserName = computed(() => {
  return `${perfil.firstName || userStore.nome || ''} ${perfil.lastName || ''}`.trim();
});

const openPasswordChangeModal = () => {
  isPasswordChangeModalOpen.value = true;
};

const handleImageError = () => {
  // Evita loop infinito: quando a imagem falha (ex.: default-avatar.png inexistente),
  // apenas zera a imagem no store para exibir iniciais; não chama carregarPerfil de novo.
  const perfilStore = usePerfilStore();
  perfilStore.profileImage = '';
};

const closePasswordChangeModal = () => {
  isPasswordChangeModalOpen.value = false;
  currentPassword.value = '';
  newPassword.value = '';
  confirmPassword.value = '';
  errorMessage.value = '';
  successMessage.value = '';
};

const handleChangePassword = async () => {
  // Limpa mensagens de erro e sucesso antes de executar
  errorMessage.value = '';
  successMessage.value = '';

  // Verifica se as senhas coincidem
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = 'As senhas não coincidem.';
    return;
  }

  // Verifica se os campos estão preenchidos
  if (!currentPassword.value || !newPassword.value || !confirmPassword.value) {
    errorMessage.value = 'Por favor, preencha todos os campos.';
    return;
  }

  try {
    // Realiza a requisição para a API de alteração de senha
    const response = await apiClient.post(
      `/api/v1/auth/change-password/`,
      {
        current_password: currentPassword.value,
        new_password: newPassword.value,
      },
    );

    // Exibe mensagem de sucesso
    successMessage.value = response.data.detail || 'Senha alterada com sucesso.';
    setTimeout(() => {
      closePasswordChangeModal(); // Fecha o modal após a alteração
    }, 2000);
  } catch (error) {
    // Lida com erros retornados pela API
    if (error.response?.status === 400) {
      errorMessage.value = error.response.data.detail || 'Erro ao alterar a senha.';
    } else if (error.response?.status === 401) {
      errorMessage.value = 'Sessão expirada. Por favor, faça login novamente.';
    } else {
      errorMessage.value = 'Erro inesperado ao alterar a senha. Tente novamente mais tarde.';
    }
  }
};

const getUserInitials = computed(() => {
  const first = perfil.firstName?.charAt(0).toUpperCase() || '';
  const last = perfil.lastName?.charAt(0).toUpperCase() || '';
  return `${first}${last}`;
});

// Acesso seguro em computeds ou templates
//const hasNotifications = computed(() => notifications.value?.length > 0);



const isActive = (href) => {
  return route.currentRoute.value.path === href;
};

// Recuperando grupos de usuário do sessionStorage
const userGroups = computed(() => userStore.grupos || []);
const userAppPermissions = computed(() => userStore.appPermissions || {});

const temAcessoItem = (item, groups) => {
  if (item.appKey) {
    return !!userAppPermissions.value[item.appKey];
  }
  if (item.visibleFor) {
    return item.visibleFor.some(group => groups.includes(group));
  }
  return true;
};

// Computando os itens de navegação com base nos grupos do usuário
const navItems = computed(() => {
  const groups = userGroups.value; // Recupera os grupos do usuário de forma reativa

  return props.customNavItems.length > 0
    ? props.customNavItems
    : [
    {
          name: 'Início',
          icon: House,
          href: '/home',
          current: false,
        },
        {
          name: 'Feed',
          icon: Cloud,
          href: '/feed',
          current: false,
          appKey: 'feed',
          beta: false
        },
        {
        name: 'Ativos',
        icon: MonitorCog,
        href: '/ativos',
        current: false,
        visibleFor: ['superadmin', 'ti'],
      },
      //{
      //  name: 'Check-In',
     //   icon: ClipboardCheck,
      //  href: '/checkin',
      //  current: false,
      //  visibleFor: ['superadmin', 'ti', 'checkin', 'admin', 'direcao'],
     // },
      

        {
          name: 'Eventos',
          icon: FileText,
          href: '/eventos',
          current: false,
          visibleFor: ['admin', 'superadmin', 'desenvolvedor', 'ti', 'direcao', 'checkin'], // Defina os tipos de usuários permitidos
        },
        {
          name: 'Eventos Adversos',
          icon: FileWarning,
          href: '/eventosadversos',
          current: false,
          appKey: 'eventos-adversos',
        },
        {
        name: 'Pendências',
        icon:  Hourglass,
        href: '/pendencias',
        current: false,
        visibleFor: ['direcao', 'superadmin', 'coordenacao'],
      },
      {
        name: 'Demandas Judiciais',
        icon: Scale,
        href: '/modulo-juridico',
        current: false,
        appKey: 'juridico',
        visibleFor: ['juridico', 'juridico_admin', 'superadmin'],
      },
        {
          name: 'Dashboard',
          icon: LayoutDashboard,
          href: '/dashboardequipamentos',
          current: false,
        },
        {
          name: 'Instâncias',
          icon: MessageCircle,
          href: '/dashboardinstancias',
          current: false,
          visibleFor: ['superadmin', 'ti'],
      },
      {
        name: 'Guardian',
        icon: Shield,
        href: '/guardian',
        current: false,
        appKey: 'guardian',
        visibleFor: ['superadmin', 'ti', 'kiosk'],
      },
      {
        name: 'Indicadores',
        icon: TrendingUp,
        href: '/indicadores',
        current: false,
        appKey: 'indicadores',
        visibleFor: ['superadmin', 'ti', 'indicadores'],
      },
      {
        name: 'SQLs Diversos',
        icon: Database,
        href: '/diversos',
        current: false,
        appKey: 'diversos',
        visibleFor: ['superadmin', 'ti', 'indicadores'],
      },
      {
        name: 'Links',
        icon: Globe,
        href: '/dashboardunidades',
        current: false,
        visibleFor: ['superadmin', 'ti', 'kiosk'],
      },
     // {
       // name: 'Documentações',
       // icon: Mail,
       // href: '/documents',
       // current: false,
       // beta: false,
      //  visibleFor: ['superadmin', 'ti', 'coordenacao','desenvolvedor'],
     // },
      {
          name: 'Prestação de Contas',
          icon: ClipboardList,
          href: '/prestacao-contas',
          current: false,
          appKey: 'prestacao-contas',
      },
      {
          name: 'Controle de Folgas',
          icon: Handshake,
          href: '/manager/daysoff',
          current: false,
          appKey: 'daysoff',
          beta: false,
          visibleFor: ['ti', 'desenvolvedor', 'superadmin'],
      },
     // {
     //   name: 'Tickets',
      //  icon: Headset,
      //  href: '/help',
      //  current: false,
      //  beta: true,
      //  visibleFor: ['superadmin', 'ti', 'desenvolvedor'],
    //  },
      {
        name: 'Configurações',
        icon: Settings,
        href: '/config',
        current: false,
        visibleFor: ['superadmin', 'ti', 'direcao', 'engenhariaclinica'],
      },
      ].filter(item => {
        return temAcessoItem(item, groups);
      });
});

const visibleNavItems = computed(() => {
  return navItems.value.filter(item => {
    // Exibe todos os itens se a propriedade `visibleFor` não existir
    if (!item.visibleFor) return true;
    // Verifica se o tipo de usuário está permitido
    return item.visibleFor.includes(userType.value);
  });
});

// Atualiza a variável CSS dinamicamente
onMounted(async () => {
  if (!Object.keys(userStore.appPermissions || {}).length) {
    await getUser();
  }
  document.documentElement.style.setProperty('--visible-items', visibleNavItems.value.length);
});

watch(visibleNavItems, (newItems) => {
  document.documentElement.style.setProperty('--visible-items', newItems.length);
});

// Função para ativar notificações
async function subscribeUser() {
  try {
    if (!("serviceWorker" in navigator)) {
      console.error("Service Worker não é suportado pelo navegador.");
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.error("Permissão para notificações negada.");
      return;
    }

    const token = await getToken(messaging, {
      vapidKey: "BAPGqPkCrJf81vFo_X--nzF-T3QLyDqFYTaXPKap-_Ar856j6zcor8AdSuFIHd7P-0EQXSRroEKs8RKZ2caQ6pU",
    });

    console.log("Token FCM:", token);

    // Gera ou recupera um device_id único para o navegador/dispositivo
    let device_id = localStorage.getItem('device_id');
    if (!device_id) {
      device_id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now();
      localStorage.setItem('device_id', device_id);
    }

    await apiClient.post(`/api/v1/notifications/register`, { token, device_id });

    // Atualiza o estado em ambos os lugares
    areNotificationsEnabled.value = true;
    perfil.setNotificationsStatus(true);
    console.log("Notificações ativadas.");
  } catch (error) {
    console.error("Erro ao inscrever o usuário:", error);
    // Em caso de erro, mantém o estado consistente
    areNotificationsEnabled.value = false;
    perfil.setNotificationsStatus(false);
  }
}

// Função para desativar notificações
async function disableNotifications() {
  try {
    const token = await getToken(messaging, {
      vapidKey: "BAPGqPkCrJf81vFo_X--nzF-T3QLyDqFYTaXPKap-_Ar856j6zcor8AdSuFIHd7P-0EQXSRroEKs8RKZ2caQ6pU",
    });

    console.log("Removendo token:", token);

    // Primeiro desativa as notificações no backend
    await apiClient.post(`/api/v1/notifications/register?disable=true`);

    // Atualiza o estado local
    areNotificationsEnabled.value = false;
    perfil.setNotificationsStatus(false);
    termoAceito.value = false; // Reseta o termo aceito também

    console.log("Notificações desativadas.");
  } catch (error) {
    console.error("Erro ao desativar notificações:", error);
    // Em caso de erro, mantém o estado consistente
    areNotificationsEnabled.value = true;
    perfil.setNotificationsStatus(true);
  }
}

// Função para alternar notificações
const toggleNotifications = async () => {
  if (areNotificationsEnabled.value) {
    await disableNotifications();
  } else {
    if (termoAceito.value) {
      await subscribeUser(); // Já aceitou antes, ativa direto
    } else {
      isTermoNotificationVisible.value = true; // Ainda não aceitou, exibe termo
    }
  }
};



const toggleSidebar = () => {
  if (window.innerWidth >= 768) {
    isCollapsed.value = !isCollapsed.value;
  } else {
    isSidebarOpen.value = !isSidebarOpen.value;
  }
};

const toggleDarkMode = () => {
  // Inicia a transição
  isTransitioning.value = true;
  
  // Aguarda um pequeno delay para iniciar a animação
  setTimeout(() => {
    // Alterna o modo
    isDark.value = !isDark.value;
    localStorage.setItem('darkMode', isDark.value);
    document.documentElement.classList.toggle('dark', isDark.value);
    
    // Finaliza a transição após a animação
    setTimeout(() => {
      isTransitioning.value = false;
    }, 700); // Duração da transição
  }, 50);
};

onMounted(() => {
  // Verifica se já existe preferência no localStorage
  const storedDarkMode = localStorage.getItem('darkMode');
  if (storedDarkMode === null) {
    // Primeiro acesso: utiliza a preferência do sistema
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    isDark.value = prefersDark;
    localStorage.setItem('darkMode', prefersDark);
  } else {
    isDark.value = storedDarkMode === 'true';
  }
  // Aplica a classe "dark" conforme o valor definido
  document.documentElement.classList.toggle('dark', isDark.value);
});

const handleLogout = () => {
  logout();
};

const messaging = getMessaging();
onMessage(messaging, (payload) => {
  console.log('Message received. ', payload);
});

// Detectar clique fora do menu
const handleClickOutside = (event) => {
  // Fecha o menu de usuário ao clicar fora
  if (
    isUserMenuOpen.value &&
    !event.target.closest('[data-user-menu]')
  ) {
    isUserMenuOpen.value = false;
  }

  // Fecha o menu de notificações ao clicar fora
  if (
    isNotificationMenuOpen.value &&
    !event.target.closest('[data-notification-menu]')
  ) {
    isNotificationMenuOpen.value = false;
  }
};

// Detectar clique fora do menu
const handleClickOutsideMobile = (event) => {
  // Fecha o menu de usuário ao clicar fora
  if (
    isMobileUserMenuOpen.value &&
    !event.target.closest('[data-user-menu-mobile]')
  ) {
    isMobileUserMenuOpen.value = false;
  }
};

// Estado para exibir a notificação
const isPopupVisible = ref(false);
const popupMessage = ref({ title: '', body: '' });

// Detecta se é mobile
const isMobile = ref(false);
onMounted(() => {
  isMobile.value = window.innerWidth <= 768; // Define se é mobile
});

// Função para exibir notificação
const showPopup = (title, body) => {
  popupMessage.value = { title, body };
  isPopupVisible.value = true;

  // Fecha automaticamente após 5s
  setTimeout(() => {
    isPopupVisible.value = false;
  }, 5000);
};

// Fecha manualmente
const closePopup = () => {
  isPopupVisible.value = false;
};

const formatNotificationPopup = (text) => {
  if (!text) return '';
  const parts = text.split('*');
  let bold = false;
  const result = [];
  for (const part of parts) {
    if (bold) {
      result.push(`<strong>${part}</strong>`);
    } else {
      result.push(part);
    }
    bold = !bold;
  }
  return result.join('');
};

// Escuta mensagens do Firebase e exibe pop-up
onMounted(() => {
  const messaging = getMessaging();
  onMessage(messaging, (payload) => {
    console.log("Mensagem recebida:", payload);

    const notificationTitle = payload?.notification?.title || "Nova Notificação";
    const notificationBody = payload?.notification?.body || "Você recebeu uma nova mensagem.";

    showPopup(notificationTitle, notificationBody);
  });
});

const toggleMobileUserMenu = () => {
  isMobileUserMenuOpen.value = !isMobileUserMenuOpen.value;
};

// Alternar menu de usuário
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
  if (isUserMenuOpen.value) {
    isNotificationMenuOpen.value = false; // Fecha notificações se abertas
  }
};

// Registrar eventos de clique
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.title = 'CCDTI - ERP';
  document.addEventListener('click', handleClickOutsideMobile);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('click', handleClickOutsideMobile);
});



const navContainer = ref(null);
const showLeftArrow = ref(false);
const showRightArrow = ref(false);

const profileImage = computed(() => {
  // Só retorna URL da imagem se for data:image ou http (evita loop com /default-avatar.png inexistente)
  if (perfil.carregado && perfil.profileImage) {
    if (
      perfil.profileImage.startsWith('data:image') ||
      perfil.profileImage.startsWith('http')
    ) {
      return perfil.profileImage;
    }
  }
  return '';
});

const logoImage = computed(() => {
  return isDark.value ? logoCcdtiBranco : logoCcdti;
});

// Estado para controlar a rolagem
const isScrolling = ref(false);
let scrollTimeout = null;

// Função para verificar a posição do scroll
const checkScrollPosition = () => {
  if (navContainer.value) {
    const { scrollLeft, scrollWidth, clientWidth } = navContainer.value;

    const isScrollable = scrollWidth > clientWidth + 5; // Margem para precisão

    if (isScrollable) {
      showLeftArrow.value = scrollLeft > 10;
      showRightArrow.value = scrollLeft + clientWidth < scrollWidth - 10;
    } else {
      showLeftArrow.value = false;
      showRightArrow.value = false;
    }
  }
};

// Função para iniciar a rolagem
const startScrolling = () => {
  isScrolling.value = true;
  if (scrollTimeout) clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    isScrolling.value = false;
  }, 300); // Ajuste o tempo conforme necessário
};

// Adiciona evento de scroll e verifica na montagem
onMounted(() => {
  checkScrollPosition();
  if (navContainer.value) {
    navContainer.value.addEventListener('scroll', startScrolling);
    navContainer.value.addEventListener('scroll', checkScrollPosition);
  }
  window.addEventListener('resize', checkScrollPosition); // Recalcula em mudanças de tamanho
});

// Remove eventos na desmontagem
onBeforeUnmount(() => {
  if (navContainer.value) {
    navContainer.value.removeEventListener('scroll', startScrolling);
    navContainer.value.removeEventListener('scroll', checkScrollPosition);
  }
  window.removeEventListener('resize', checkScrollPosition);
});
</script>
